// src/pages/public/ActivateBeta.jsx
// Página pública (sin auth) en /activate-beta. Canjea el token del email día 3
// contra la Cloud Function claimFreeTrialToken y muestra el resultado.
//
// El canje es token-based: funciona con o sin sesión (el token está atado al
// uid en Firestore). Si el usuario NO está logueado, tras el éxito lo mandamos
// a /login para que entre y vea el dashboard (ya con acceso activo).
import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import EELogo from '../../components/EELogo';

const CLAIM_URL =
  'https://southamerica-east1-mati-edge-elite.cloudfunctions.net/claimFreeTrialToken';

const ORANGE = '#FF6B2C';

export default function ActivateBeta() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const token = searchParams.get('token');
  const [status, setStatus] = useState('loading'); // loading | success | error
  const claimedRef = useRef(false);

  useEffect(() => {
    if (!token) {
      navigate('/checkout', { replace: true });
      return;
    }
    if (claimedRef.current) return;
    claimedRef.current = true;

    (async () => {
      try {
        const resp = await fetch(CLAIM_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
        const data = await resp.json().catch(() => ({}));
        setStatus(resp.ok && data.success ? 'success' : 'error');
      } catch (_e) {
        setStatus('error');
      }
    })();
  }, [token, navigate]);

  const goDashboard = () => {
    if (!authLoading && !user) navigate('/login', { replace: true });
    else navigate('/dashboard', { replace: true });
  };

  return (
    <div className="mobile-root">
      <section
        className="screen"
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          textAlign: 'center',
        }}
      >
        <div style={{ marginBottom: 32 }}>
          <EELogo size={26} />
        </div>

        {status === 'loading' && (
          <>
            <div
              style={{
                width: 48,
                height: 48,
                border: `4px solid rgba(255,107,44,.2)`,
                borderTopColor: ORANGE,
                borderRadius: '50%',
                animation: 'ab-spin 0.8s linear infinite',
                marginBottom: 22,
              }}
            />
            <p style={{ fontSize: 16, fontWeight: 600, color: 'var(--ink)', margin: 0 }}>
              Activando tu acceso...
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                background: '#10B981',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 22,
              }}
            >
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
                <path d="M5 12l5 5 9-11" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, margin: '0 0 8px', letterSpacing: '-0.02em' }}>
              ¡Listo! Tienes 3 días de acceso completo.
            </h1>
            <p style={{ fontSize: 14, color: 'var(--muted)', margin: '0 0 26px', lineHeight: 1.5 }}>
              El motor V6 en vivo, 64+ ligas y todas las señales del dashboard.
            </p>
            <button
              className="btn-primary accent"
              style={{ maxWidth: 320, background: ORANGE }}
              onClick={goDashboard}
            >
              Ir al dashboard →
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                background: '#F5F5F7',
                border: '1px solid #E4E4E9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 22,
              }}
            >
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#9A9AA1" strokeWidth="2.4">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 8v5M12 16.5v.01" strokeLinecap="round" />
              </svg>
            </div>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, margin: '0 0 8px', letterSpacing: '-0.02em' }}>
              Este link ya fue usado o venció.
            </h1>
            <p style={{ fontSize: 14, color: 'var(--muted)', margin: '0 0 26px', lineHeight: 1.5 }}>
              Puedes empezar con un plan y acceder al motor completo.
            </p>
            <button
              className="btn-primary accent"
              style={{ maxWidth: 320, background: ORANGE }}
              onClick={() => navigate('/checkout')}
            >
              Ver planes →
            </button>
          </>
        )}

        <style>{`@keyframes ab-spin { to { transform: rotate(360deg); } }`}</style>
      </section>
    </div>
  );
}
